import React from 'react';
import { Link } from 'react-router-dom';

const Privacy: React.FC = () => {
  return (
    <div className="min-h-screen bg-[#F3F3F3] text-[#132D28]">
      <header className="fixed top-0 w-full bg-[#F3F3F3]/90 backdrop-blur-sm border-b border-[#204941]/10 z-40">
        <nav className="container mx-auto py-4">
          <Link to="/" className="text-lg font-semibold text-[#204941]">Watchi.app</Link>
        </nav>
      </header>

      <main className="container mx-auto pt-28 pb-16">
        <article className="bg-white rounded-3xl shadow-sm px-6 md:px-12 py-10">
          <header className="mb-10">
            <h1 className="text-3xl md:text-4xl font-semibold text-[#132D28]">Privacy Promise for Watchi.app</h1>
            <p className="mt-4 text-sm text-[#204941]/60">Effective as of 03 October 2025</p>
          </header>

          <section className="space-y-8 text-[#204941]/80">
            <div>
              <h2 className="text-xl font-semibold text-[#204941] mb-3">1. Why this policy exists</h2>
              <p>
                Watchi.app is built for personal creativity, not personal profiling. This document explains how we collect, use, and store information when you browse our library of watch faces, wallpapers, and widgets.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-[#204941] mb-3">2. Information we handle</h2>
              <ul className="list-disc pl-5 space-y-2">
                <li>Apple ID details shared by Apple if you subscribe or sign in with Apple.</li>
                <li>Purchase and restore history so your premium collections stay unlocked.</li>
                <li>Diagnostic data (crashes, feature usage) to understand what to improve next.</li>
                <li>Optional creator submissions — imagery, URLs, and captions you choose to upload.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-[#204941] mb-3">3. How we use your data</h2>
              <ul className="list-disc pl-5 space-y-2">
                <li>Deliver your selected watch faces and wallpapers to the Watch app.</li>
                <li>Keep your favorites and downloads synced across devices connected to iCloud.</li>
                <li>Send optional tips and release notes if you opt in to email updates.</li>
                <li>Monitor abuse, spam, or unauthorized redistribution of Watchi.app content.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-[#204941] mb-3">4. Legal bases</h2>
              <p>
                We process information to perform the services you install Watchi.app for, comply with applicable regulations, and pursue our legitimate interest in operating a safe and reliable platform. Where consent is required, we will ask before collecting data.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-[#204941] mb-3">5. Third parties we rely on</h2>
              <p>The app may share limited data with trusted providers:</p>
              <ul className="list-disc pl-5 space-y-2 mt-2">
                <li>Apple for payment processing, subscriptions, and analytics.</li>
                <li>Secure cloud storage to keep your library accessible.</li>
                <li>Email partners for newsletters, if you subscribe.</li>
              </ul>
              <p className="mt-3">We never sell personal information or run third-party ads.</p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-[#204941] mb-3">6. Retention</h2>
              <p>
                Personal data linked to purchases is preserved as long as your Watchi.app account remains active. You can request deletion anytime and we will remove identifiable records unless laws require otherwise.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-[#204941] mb-3">7. Your options</h2>
              <ul className="list-disc pl-5 space-y-2">
                <li>Download a copy of your data by contacting us via email.</li>
                <li>Ask us to correct, limit, or erase information we hold.</li>
                <li>Switch off personalized notifications within the app.</li>
                <li>Opt out of newsletters directly from any message footer.</li>
              </ul>
              <p className="mt-3">Write to <a href="mailto:support@watchi.app" className="text-[#204941] underline hover:text-[#68C1AE]">support@watchi.app</a> to start any request.</p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-[#204941] mb-3">8. International transfers</h2>
              <p>
                We operate from the European Union and may store data on servers in the EU and United States. When data travels across borders, we rely on legal safeguards such as Standard Contractual Clauses.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-[#204941] mb-3">9. Kids and Watchi.app</h2>
              <p>
                Watchi.app is intended for users aged 13 and up. We do not knowingly collect personal data from children under this age threshold.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-[#204941] mb-3">10. Updates</h2>
              <p>
                If we adjust how Watchi.app handles data, we will refresh this page and note the revision date. Significant changes will be announced in-app or via email.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-[#204941] mb-3">11. Talk to us</h2>
              <p>
                Send privacy questions or requests to <a href="mailto:support@watchi.app" className="text-[#204941] underline hover:text-[#68C1AE]">support@watchi.app</a>. We aim to respond within 7 business days.
              </p>
            </div>
          </section>
        </article>
      </main>
    </div>
  );
};

export default Privacy;